/**
 * «Τι μελέτη θέλετε;» — the starting settings of a new project, chosen in the
 * dialog that File → New opens.
 *
 * DEFAULT_CONFIG is NOT changed: it is merged under every saved project when it
 * is loaded, so a change there would quietly change existing studies. A preset
 * is applied once, to the configuration a NEW project starts from, and is then
 * an ordinary configuration the user can edit like any other.
 *
 * The values are the defaults agreed for behind-the-meter studies of Greek
 * customers: 20 years, 6% nominal discount rate, 2% inflation, no loan, a
 * 440 W 54-cell module on roof frames at 30° facing south, the inverter left
 * to auto-design, low voltage 400 V, simplified costing, one total price per
 * kWh and the consumption entered as one annual figure.
 */
import { applyAssetType, assetTypeDefinition } from "../../utils/projectAssets";
import { setPathInObject } from "../../utils/windConfig";
import { enableDemandForBtmSelection } from "./consumptionReadiness";
import { alignBtmSwitches, CONSUMPTION_MONTH_NAMES } from "./btm";
import { initialDemandShape } from "./demandShape";
import { defaultDemandWeights } from "./demandProfileDefaults";
import { DEFAULT_PROJECT_DEBT } from "./projectDebt";
import { SINGLE_ENERGY_RATE } from "./retailTariff";

export const STUDY_KINDS = ["net_billing", "zero_feed_in", "pv_battery", "other"];
export const STUDY_PRESET_MODULE = "Jinko JKM-N-54HL4R-B-440";
export const DEFAULT_PRESET_PV_KWP = 10;
export const CONSUMER_TYPES = ["Commercial", "Residential Urban"];
// Indicative Greek market prices for small rooftop systems, before VAT.
// Published 2026 ranges for a 10 kWp grid-tied rooftop system run from about
// 8,000-12,000 EUR to 13,000-17,000 EUR including 24% VAT, i.e. roughly
// 650-1,370 EUR/kWp before VAT; 900 is near the middle. Batteries are quoted
// at about 600-700 EUR/kWh. The user replaces them with the installer's price.
export const PRESET_PRICES = { pvEurPerKwp: "900", pvOmPctYear: "1", batteryEurPerKwh: "550", batteryOmPctYear: "1" };

/**
 * The prices a quick study starts with, from what the dialog was given: a
 * price must be above 0 and a maintenance share 0 or more, otherwise the
 * indicative figure above is used. Stored as the Economics page stores them.
 */
export function studyPrices(prices = {}) {
  const read = (value) => Number(String(value ?? "").trim().replace(",", "."));
  const price = (value, fallback) => {
    const n = read(value);
    return String(value ?? "").trim() !== "" && Number.isFinite(n) && n > 0 ? String(n) : fallback;
  };
  const share = (value, fallback) => {
    const n = read(value);
    return String(value ?? "").trim() !== "" && Number.isFinite(n) && n >= 0 ? String(n) : fallback;
  };
  return {
    pvEurPerKwp: price(prices?.pvEurPerKwp, PRESET_PRICES.pvEurPerKwp),
    pvOmPctYear: share(prices?.pvOmPctYear, PRESET_PRICES.pvOmPctYear),
    batteryEurPerKwh: price(prices?.batteryEurPerKwh, PRESET_PRICES.batteryEurPerKwh),
    batteryOmPctYear: share(prices?.batteryOmPctYear, PRESET_PRICES.batteryOmPctYear),
  };
}

/**
 * Which simplified-costing prices of a project are still the indicative ones
 * above (PV 900 €/kWp, battery 550 €/kWh), so the results can say so before an
 * offer goes out. Empty for detailed costing and for prices of the user's own.
 * @returns {("pv"|"battery")[]}
 */
export function indicativePricesInUse(config) {
  const costs = config?.feasibility?.economics?.investmentCosts || {};
  if (String(costs.costingMode || "").trim().toLowerCase() !== "simplified") return [];
  const same = (value, preset) => String(value ?? "").trim() !== ""
    && Number(String(value).replace(",", ".")) === Number(preset);
  const type = assetTypeDefinition(config?.systemType);
  const hasPv = Boolean(type?.hasPv);
  const hasBattery = Boolean(type?.hasBess);
  return [
    ...(hasPv && same(costs.simplePvEurPerKwp, PRESET_PRICES.pvEurPerKwp) ? ["pv"] : []),
    ...(hasBattery && same(costs.simpleBatteryEurPerKwh, PRESET_PRICES.batteryEurPerKwh) ? ["battery"] : []),
  ];
}

/** The text of the dialog's cards, in reading order. */
export function studyKindOptions(isGreek) {
  const tr = (el, en) => (isGreek ? el : en);
  const options = [
    { value: "net_billing", title: tr("Φ/Β με net billing", "PV with net billing"),
      text: tr("Αυτοκατανάλωση με συμψηφισμό: η ενέργεια που περισσεύει εγχέεται στο δίκτυο και πιστώνεται στον λογαριασμό.",
        "Self-consumption with net billing: surplus energy is exported and credited on the bill.") },
    { value: "zero_feed_in", title: tr("Φ/Β χωρίς έγχυση (μηδενική έγχυση)", "PV with zero feed-in"),
      text: tr("Αυτοκατανάλωση χωρίς έγχυση: καμία ενέργεια δεν εγχέεται στο δίκτυο, η περίσσεια περικόπτεται.",
        "Self-consumption without export: no energy is exported, the surplus is curtailed.") },
    { value: "pv_battery", title: tr("Φ/Β με μπαταρία", "PV with battery"),
      text: tr("Αυτοκατανάλωση με net billing και μπαταρία. Η μπαταρία επιλέγεται στη Ρύθμιση Μονάδας → Αποθήκευση.",
        "Self-consumption with net billing and a battery. The battery is chosen under Unit Setup → Storage.") },
  ];
  return options;
}

// How the array is mounted, as the quick study asks it. "roof_frame" frames on
// a flat roof (the roof itself flat, facing south); "roof_flush" modules lying
// on a pitched roof, whose pitch and facing are the array's; "ground".
export const MOUNTING_TYPES = ["roof_frame", "roof_flush", "ground"];
export const DEFAULT_TILT = 30;
// Facings offered by name (degrees clockwise from north).
export const FACINGS = [180, 135, 225, 90, 270];
export function facingLabel(azimuth, isGreek) {
  const el = { 180: "Νότος", 135: "Νοτιοανατολικά", 225: "Νοτιοδυτικά", 90: "Ανατολή", 270: "Δύση" };
  const en = { 180: "South", 135: "South-east", 225: "South-west", 90: "East", 270: "West" };
  return (isGreek ? el : en)[azimuth] || `${azimuth}°`;
}
// Row and near shading of a quick study, as one annual percentage: the layout
// scene is not needed then, only the electrical design.
export const PRESET_SHADING_PCT = 3;

function positiveNumber(value, fallback) {
  const out = Number(value);
  return Number.isFinite(out) && out > 0 ? out : fallback;
}

/**
 * The configuration a new project starts from.
 *
 * @param {object} base   the configuration to start from (DEFAULT_CONFIG)
 * @param {{kind: string, useCase?: string, pvKwp?: number|string, isGreek?: boolean}} choice
 * @returns {object} a new configuration; `base` is never changed. "other" and
 *   an unknown kind return `base` itself, i.e. exactly today's new project.
 */
// The per-battery operating defaults the Storage tab gives a catalog battery
// when it is selected there (BESSForm DEFAULT_BATTERY_OVERRIDES).
const SELECTED_BATTERY_OVERRIDES = { iniSoC: 50, startDaySoC: 70, dailyCycles: 1, minIdleMins: 0, eol_soh_pct: 80 };

/** A catalog battery ({name, energy, p_max_c, p_max_d}) as the Storage tab stores a selection. */
export function selectedCatalogBattery(battery) {
  const name = String(battery?.battery_name ?? battery?.name ?? "").trim();
  if (!name) return null;
  const id = battery?.id ?? battery?.battery_id ?? null;
  return {
    _key: id != null ? `id:${id}` : `name:${name}`,
    id,
    name,
    energy: battery?.energy ?? null,
    p_max_c: battery?.p_max_c ?? null,
    p_max_d: battery?.p_max_d ?? null,
    count: 1,
    overrides: { ...SELECTED_BATTERY_OVERRIDES },
  };
}

/** A typed number (decimal comma or point) as the page stores it, or "" when not usable. */
function storedNumber(value) {
  const n = Number(String(value ?? "").trim().replace(",", "."));
  return Number.isFinite(n) && n > 0 ? String(n) : "";
}

export function applyStudyPreset(base, {
  kind, useCase, pvKwp, isGreek, name, annualKwh, priceEurKwh, battery,
  module, inverter, mounting, tilt, azimuth, prices,
} = {}) {
  if (!["net_billing", "zero_feed_in", "pv_battery"].includes(kind)) return base;
  const consumer = CONSUMER_TYPES.includes(useCase) ? useCase : "Commercial";
  const kwp = positiveNumber(pvKwp, DEFAULT_PRESET_PV_KWP);
  let cfg = applyAssetType(base, kind === "pv_battery" ? "Hybrid" : "Standalone PV");
  const set = (path, value) => { cfg = setPathInObject(cfg, path, value); };

  // The report in the language the user works in.
  set("reportLanguage", isGreek ? "el" : "en");

  // Behind the meter, set the way the General page sets it.
  cfg = enableDemandForBtmSelection(setPathInObject(cfg, "settlementType", "BTM"));
  set("btm.gridInjection", { ...(cfg.btm?.gridInjection || {}), mode: kind === "zero_feed_in" ? "zero_feed_in" : "allowed" });
  set("btm.netBilling", { ...(cfg.btm?.netBilling || {}), enabled: kind !== "zero_feed_in" });

  // Consumption: one annual figure, shaped by the consumer type.
  set("useCase", consumer);
  const demand = cfg.feasibility?.technical?.demand || {};
  set("feasibility.technical.demand", {
    ...demand,
    enabled: true,
    inputType: "Annual Consumption (kWh)",
    weights: defaultDemandWeights(consumer),
    shape: initialDemandShape(consumer),
  });

  // Tariff: one total price per kWh, one rate all day.
  const tariffs = cfg.feasibility?.technical?.tariffs || {};
  set("feasibility.technical.tariffs", {
    ...tariffs,
    rateMode: "legacy_total",
    legacyPricingMode: SINGLE_ENERGY_RATE,
    itemized: { ...(tariffs.itemized || {}), pricingMode: SINGLE_ENERGY_RATE },
    billingScheme: "Single zonal",
    single: { ...(tariffs.single || {}), peakStart: "00:00", peakEnd: "23:00" },
  });

  // Low-voltage connection.
  set("grid", { ...(cfg.grid || {}), connectionLevel: "LV", voltageV: 400 });

  // PV: the size is the auto-design target; the inverter is chosen by auto-design.
  set("pvInstalledCap", { ...(cfg.pvInstalledCap || {}), kw: kwp });
  // New quick studies search ±10% around the target, preferring the closest
  // compatible whole-panel arrangement. This is never applied when loading.
  set("autoDesign", { ...(cfg.autoDesign || {}), targetCapacityKw: kwp, undersizeRatio: 0.9, oversizeRatio: 1.1 });
  // A module or inverter the user chose is kept by auto-design; none chosen,
  // the inverter is the engine's pick.
  set("read_module", String(module || "").trim() || STUDY_PRESET_MODULE);
  set("read_inverter", String(inverter || "").trim());
  const mount = MOUNTING_TYPES.includes(mounting) ? mounting : "roof_frame";
  const tiltDeg = Number.isFinite(Number(tilt)) && Number(tilt) >= 0 && Number(tilt) < 90 && String(tilt ?? "").trim() !== ""
    ? Number(tilt) : DEFAULT_TILT;
  const azimDeg = Number.isFinite(Number(azimuth)) && String(azimuth ?? "").trim() !== "" ? Number(azimuth) : 180;
  set("panelOrientation", {
    ...(cfg.panelOrientation || {}),
    installationType: mount,
    tilt: tiltDeg,
    azim: azimDeg,
    // Frames stand on a flat roof; a flush array lies on the roof, so the
    // roof's pitch and facing are the array's own.
    ...(mount === "roof_frame" ? { roofPitch: 0, roofAzimuth: 180 } : {}),
    ...(mount === "roof_flush" ? { roofPitch: tiltDeg, roofAzimuth: azimDeg } : {}),
  });
  // Shading as one annual percentage (the engine then ignores row geometry
  // and the saved layout scene; the report says so).
  set("pvLosses", {
    ...(cfg.pvLosses || {}),
    dc: { ...(cfg.pvLosses?.dc || {}), shadingMode: "percentage", shading: PRESET_SHADING_PCT },
  });

  // Economics.
  set("feasibility.settlementPeriod", "20");
  // Turnkey prices, before VAT: the user's from the dialog, or the indicative
  // ones, so a first run never meets an empty (zero) investment.
  const chosen = studyPrices(prices);
  set("feasibility.economics.investmentCosts", {
    ...(cfg.feasibility?.economics?.investmentCosts || {}),
    costingMode: "simplified",
    simplePvEurPerKwp: chosen.pvEurPerKwp,
    simplePvOmPctYear: chosen.pvOmPctYear,
    ...(kind === "pv_battery" ? {
      simpleBatteryEurPerKwh: chosen.batteryEurPerKwh,
      simpleBatteryOmPctYear: chosen.batteryOmPctYear,
    } : {}),
  });
  set("feasibility.economics.financialParams", {
    ...(cfg.feasibility?.economics?.financialParams || {}),
    nominalDiscountRatePctYear: "6.0",
    inflationRatePctYear: "2.0",
    debtPercent: "0",
  });
  set("feasibility.economics.projectDebt", { ...DEFAULT_PROJECT_DEBT, ...(cfg.feasibility?.economics?.projectDebt || {}), debtPercent: 0 });

  // The customer's own figures, when the dialog was given them. Each lands in
  // the field the pages show, so the project reads as if typed there.
  const clientName = String(name || "").trim();
  if (clientName) set("clientInfo", { ...(cfg.clientInfo || {}), clName: clientName });
  const kwh = storedNumber(annualKwh);
  if (kwh) set("feasibility.technical.demand.monthly", { "Jan-Dec": kwh });
  const price = storedNumber(priceEurKwh);
  if (price) {
    const tariffsNow = cfg.feasibility?.technical?.tariffs || {};
    set("feasibility.technical.tariffs.monthly", Object.fromEntries(CONSUMPTION_MONTH_NAMES.map((month) => [month, {
      ...(tariffsNow.monthly?.[month] || {}), peak: price, offPeak: price,
    }])));
  }
  const selected = kind === "pv_battery" ? selectedCatalogBattery(battery) : null;
  if (selected) set("bess", { ...(cfg.bess || {}), selected: [selected], activeName: selected.name });

  // Every BESS-page switch that behind-the-meter implies, as the General page does.
  return alignBtmSwitches(cfg);
}
